import React from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  MapPin,
  Phone,
  ChevronRight,
} from 'lucide-react';
import styles from './Footer.module.css';

const QUICK_LINKS = [
  { name: 'About Us', href: '/about' },
  { name: 'Services', href: '/services' },
  { name: 'All Products', href: '/products' },
  { name: 'FAQs', href: '/faq' },
  { name: 'Contact', href: '/contact' },
];

const LEGAL_LINKS = [
  { name: 'Terms & Conditions', href: '/terms', type: 'route' },
  { name: 'Privacy Policy', href: '/privacy', type: 'route' },
  {
    name: 'Certificate',
    href: '/images/certificate/Bronze.pdf',
    type: 'download',
    fileName: 'Bronze-Certificate.pdf',
  },
];

const SOCIAL_LINKS = [
  { name: 'Facebook', label: 'f', href: 'https://www.facebook.com/share/1CkR5ZJoSm/?mibextid=wwXIfr' },
  { name: 'Twitter', label: 'X', href: '#' },
  { name: 'LinkedIn', label: 'in', href: '#' },
];

const PHONE_PRIMARY = '+919557005492';
const PHONE_SECONDARY = '+919759599922';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className={styles.footer}>
      {/* Main footer */}
      <div className={styles.body}>
        <span className={styles.watermark} aria-hidden="true">
          GREENERI
        </span>
        <div className={styles.bodyGlow} aria-hidden="true" />

        <div className={styles.container}>
          <div className={styles.layout}>
            {/* Brand column */}
            <div className={styles.brandCol}>
              <Link to="/" className={styles.logoWrap}>
                <img src="/logo.png" alt="Greeneri Paper Tradelinks" className={styles.logo} />
              </Link>

              <h3 className={styles.brandHeadline}>
                Your partner in{' '}
                <span className={styles.headlineAccent}>premium paper</span> solutions
              </h3>

              <span className={styles.brandDesc}>
                PE coated rolls, food wrap, cup sleeves &amp; custom grades — manufactured in
                Meerut with consistent quality for brands across India.
              </span>

              <div className={styles.socialRow}>
                <span className={styles.socialLabel}>Connect</span>
                <div className={styles.socialList}>
                  {SOCIAL_LINKS.map((s) => (
                    <a
                      key={s.name}
                      href={s.href}
                      className={styles.socialBtn}
                      aria-label={s.name}
                      target={s.href.startsWith('http') ? '_blank' : undefined}
                      rel={s.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    >
                      {s.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Links + contact — open layout, no container box */}
            <nav className={styles.navArea} aria-label="Footer navigation">
              <div className={styles.navCol}>
                <h4 className={styles.navTitle}>Quick Links</h4>
                <ul className={styles.linkList}>
                  {QUICK_LINKS.map((link) => (
                    <li key={link.href}>
                      <Link to={link.href} className={styles.footerLink}>
                        <ChevronRight size={14} strokeWidth={2.5} aria-hidden="true" />
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className={styles.navCol}>
                <h4 className={styles.navTitle}>Legal</h4>
                <ul className={styles.linkList}>
                  {LEGAL_LINKS.map((link) => (
                    <li key={link.href}>
                      {link.type === 'download' ? (
                        <a
                          href={link.href}
                          className={styles.footerLink}
                          download={link.fileName}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ChevronRight size={14} strokeWidth={2.5} aria-hidden="true" />
                          {link.name}
                        </a>
                      ) : (
                        <Link to={link.href} className={styles.footerLink}>
                          <ChevronRight size={14} strokeWidth={2.5} aria-hidden="true" />
                          {link.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div className={styles.navCol}>
                <h4 className={styles.navTitle}>Reach Us</h4>
                <ul className={styles.contactList}>
                  <li>
                    <div className={styles.contactItem}>
                      <Phone size={18} strokeWidth={2} aria-hidden="true" />
                      <span className={styles.contactText}>
                        <span className={styles.contactLabel}>Sales &amp; support</span>
                        <span className={styles.contactValue}>
                          <a href={`tel:${PHONE_PRIMARY}`}>+91 9557005492</a>
                          <span className={styles.slash}>/</span>
                          <a href={`tel:${PHONE_SECONDARY}`}>+91 9759599922</a>
                        </span>
                      </span>
                    </div>
                  </li>
                  <li>
                    <a href="mailto:info@greeneripapers.com" className={styles.contactItem}>
                      <Mail size={18} strokeWidth={2} aria-hidden="true" />
                      <span className={styles.contactText}>
                        <span className={styles.contactLabel}>Email</span>
                        <span className={styles.contactValue}>info@greeneripapers.com</span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <div className={styles.contactItem}>
                      <MapPin size={18} strokeWidth={2} aria-hidden="true" />
                      <span className={styles.contactText}>
                        <span className={styles.contactLabel}>Factory</span>
                        <span className={styles.contactValue}>
                          Khasra 187, Village Kanshi, Gagol Road, Partapur, Meerut — 250103
                        </span>
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </nav>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className={styles.bottom}>
        <div className={styles.container}>
          <div className={styles.bottomInner}>
            <span className={styles.copy}>
              © {currentYear} Greeneri Paper Tradelinks Pvt. Ltd.
            </span>
            <div className={styles.bottomNav}>
              <Link to="/terms">Terms</Link>
              <Link to="/privacy">Privacy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
